import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  LayoutDashboard, Users as UsersIcon, Truck, FileText, ShoppingCart, PackageSearch, LogOut, Boxes, UserCog, ScrollText, TrendingUp, KeyRound, Calculator, Settings as SettingsIcon, Ship,
} from "lucide-react";
import { Logo } from "../components/Logo";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/customers", label: "Customers", icon: UsersIcon, testid: "nav-customers" },
  { to: "/suppliers", label: "Suppliers", icon: Truck, testid: "nav-suppliers" },
  { to: "/items", label: "Items", icon: Boxes, testid: "nav-items" },
  { to: "/offers", label: "Offers", icon: FileText, testid: "nav-offers" },
  { to: "/orders", label: "Orders", icon: ShoppingCart, testid: "nav-orders" },
  { to: "/order-items", label: "Order Items", icon: PackageSearch, testid: "nav-order-items" },
  { to: "/imports", label: "Imports", icon: Ship, testid: "nav-imports" },
  { to: "/reports", label: "Sale Report", icon: TrendingUp, testid: "nav-reports" },
  { to: "/accounting", label: "Accounting", icon: Calculator, testid: "nav-accounting" },
  { to: "/users", label: "Users", icon: UserCog, testid: "nav-users" },
  { to: "/logs", label: "Activity Logs", icon: ScrollText, testid: "nav-logs" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

function ChangePasswordDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const reset = () => setForm({ current_password: "", new_password: "", confirm: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) return toast.error("New password must be at least 6 characters");
    if (form.new_password !== form.confirm) return toast.error("New passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="rounded-none max-w-md">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Change your password</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="label-caps">Current password</Label>
            <Input
              type="password"
              required
              autoFocus
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              className="rounded-none mt-1"
              data-testid="cp-current"
            />
          </div>
          <div>
            <Label className="label-caps">New password (min 6 chars)</Label>
            <Input
              type="password"
              required
              minLength={6}
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              className="rounded-none mt-1"
              data-testid="cp-new"
            />
          </div>
          <div>
            <Label className="label-caps">Confirm new password</Label>
            <Input
              type="password"
              required
              minLength={6}
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              className="rounded-none mt-1"
              data-testid="cp-confirm"
            />
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={busy}
              className="rounded-none bg-blue-600 hover:bg-blue-700"
              data-testid="cp-submit"
            >
              {busy ? "Updating…" : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="app-layout">
      {/* Sidebar */}
      <aside className="w-56 xl:w-64 bg-slate-900 text-slate-300 flex flex-col fixed inset-y-0 left-0 z-30">
        <div className="px-5 py-5 border-b border-slate-800 flex items-center gap-3">
          <div className="bg-white px-2.5 py-2 flex items-center">
            <Logo height={22} />
          </div>
          <div className="label-caps text-slate-500">OMS · v1</div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {nav.map(({ to, label, icon: Icon, testid, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm rounded-none transition-colors duration-200 ${
                  isActive
                    ? "bg-slate-800 text-white border-l-2 border-blue-500"
                    : "text-slate-400 hover:text-white hover:bg-slate-800/50 border-l-2 border-transparent"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-800">
          <div className="px-3 py-2 mb-2">
            <div className="label-caps text-slate-500">Signed in</div>
            <div className="text-sm text-white truncate" data-testid="current-user-email">{user?.email}</div>
          </div>
          <button
            onClick={() => setPwOpen(true)}
            data-testid="change-password-btn"
            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors duration-200"
          >
            <KeyRound className="w-4 h-4" /> Change password
          </button>
          <button
            onClick={doLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors duration-200"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 ml-56 xl:ml-64 min-w-0">
        <Outlet />
      </main>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
